export class HttpError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * The request got no HTTP response: offline, DNS, refused or dropped connection.
 * A cancelled request (AbortError) is not one; it propagates unchanged.
 */
export class NetworkError extends Error {
  constructor() {
    super("No response from the server");
    this.name = "NetworkError";
  }
}

export interface HttpResponse<T> {
  status: number;
  data: T;
}

/** `fetch` with the same arguments; a failure without a response becomes NetworkError. */
export async function request(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if ((err as { name?: unknown } | null)?.name === "AbortError") throw err;
    throw new NetworkError();
  }
}

/** Parsed body of a response: JSON when it says so, else text; undefined when unreadable. */
async function readBody(res: Response): Promise<{ body: unknown; readable: boolean }> {
  const contentType = res.headers.get("content-type") || "";
  try {
    return { body: contentType.includes("application/json") ? await res.json() : await res.text(), readable: true };
  } catch {
    return { body: undefined, readable: false };
  }
}

function errorFor(res: Response, body: unknown): HttpError {
  const msg = (typeof body === "object" && body && "error" in (body as any)) ? String((body as any).error) : res.statusText;
  return new HttpError(msg || `HTTP ${res.status}`, res.status, body);
}

/** The HttpError of a non-2xx response, for the few callers that need the raw Response (blob downloads, form posts). */
export async function responseError(res: Response): Promise<HttpError> {
  return errorFor(res, (await readBody(res)).body);
}

/**
 * Same request/error semantics as `http`, but the HTTP status survives.
 *
 * Needed by endpoints where a success status is load-bearing rather than
 * decorative — the telemetry lake answers 202 with a query id when Athena
 * misses the inline deadline, and a client that only sees the body cannot tell
 * that apart from a finished answer.
 */
export async function httpWithStatus<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<HttpResponse<T>> {
  const res = await request(input, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.headers || {}),
    },
  });

  const { body, readable } = await readBody(res);
  // A success status with a body that is malformed or cut off (connection reset,
  // page navigating away) is a failed request, never a success with no data.
  if (res.ok && !readable) throw new HttpError(`Unreadable response body (HTTP ${res.status})`, res.status);

  if (!res.ok) throw errorFor(res, body);

  return { status: res.status, data: body as T };
}

export async function http<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<T> {
  const { data } = await httpWithStatus<T>(input, init);
  return data;
}
